import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Table, Card, Pagination, Tooltip } from "antd";
import { InfoCircleFilled, PlusOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { useGetBreakpoints } from "../../hooks/useGetBreakpoints";
import { ItemDetailMobile } from "../../components/ItemDetailMobile";
import { isArrayNonEmpty } from "../../util/ApiHelper";
import ListEmptyComponent from "../../components/ListEmptyComponent/ListEmptyComponent";
import "./BusinessCardRequests.css";
import { formatDate } from "../../util/helper";
import { axiosInstance, BASE_API_CONFIG, STATUS_CODE } from "../../util/Api";
import { useDispatch, useSelector } from "react-redux";
import { fetchStart, fetchSuccess } from "../../appRedux/actions";
import { BUSINESS_REQUEST_STATUS, BusinessRequestStatus } from "../../util/Constant";

const BusinessCardRequestsFreightForwarder = () => {
  const history = useHistory();
  const { isMobile } = useGetBreakpoints();
  const [businessCardData, setBusinessCardData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const dispatch = useDispatch();
  const { loading } = useSelector(({ common }) => common);
  

  useEffect(() => {
      fetchBusinessCardRequests();
  }, []);

  const fetchBusinessCardRequests = async () => {
    dispatch(fetchStart());
    try {
      const response = await axiosInstance.get(
        `${BASE_API_CONFIG.baseURL}/business-card-request`
      );
      setBusinessCardData(response.data);
      setTotalCount(response.data.totalCount);
    } catch (error) {
      console.error("Error fetching business card requests:", error);
    } finally {
      dispatch(fetchSuccess());
    }
  };
  const onShowSizeChange = useCallback(
    (current, pageSize) => {
      setCurrentPage(current);
      setPageSize(pageSize);
      fetchBusinessCardRequests({ page: current, limit: pageSize });
    },
    []
  );

  const handleSendBusinessRequest = useCallback(() => {
    history.push("/business-card-requests-freightforwarder/send-business-card");
  }, [history]);

  const handleRowClick = useCallback((id) => {
    // TODO: Navigate to business card request details
    // history.push(`/business-card-requests-freightforwarder/${id}`);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "#faad14";
      case "approved":
        return "#52c41a";
      case "rejected":
        return "#f5222d";
      default:
        return "#1890ff";
    }
  };


  const StatusBadge = ({ status }) => {
    const isPending = status === BUSINESS_REQUEST_STATUS.PENDING;

    const statusText = isPending
      ? "Pending For Approval"
      : status;

    return (
      <span
        style={{
          color: getStatusColor(status),
          fontWeight: "bold",
          padding: "4px 8px",
          borderRadius: "4px",
          backgroundColor: getStatusColor(status) + "20",
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        {statusText}

        {isPending && (
          <Tooltip title="Awaiting exporter approval">
            <InfoCircleFilled style={{ cursor: "pointer" }} />
          </Tooltip>
        )}
      </span>
    );
  };



  const columns = useMemo(
    () => [
      {
        title: "Date",
        key: "createdDate",
        width: "100px",
        render: (record) => <div>{formatDate(record?.createdAt)}</div>,
      },
      {
        title: "Exporter Company Name",
        key: "companyName",
        width: "150px",
        render: (record) => <div>{record?.exporter.companyName}</div>,
      },
      {
        title: "Status",
        key: "status",
        width: "120px",
        render: (record) => <StatusBadge status={record?.status} />
      },
    ],
    []
  );

  const renderContent = useCallback(
    (data, count) => {
      return isMobile ? (
        <>
          <Card bodyStyle={{ padding: 0 }}>
            {data.map((record) => (
              <ItemDetailMobile
                key={record.id}
                postedDate={formatDate(record.createdAt)}
                primaryRow={{
                  text: record.exporter.companyName,
                }}
                status={record.status}
                StatusComponent={({ status }) => (
                  <StatusBadge status={status} />
                )}
                handleNavigation={() => {
                  handleRowClick(record.id);
                }}
              />
            ))}
          </Card>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={count || data?.length || 0}
            onChange={onShowSizeChange}
            showSizeChanger
            style={{ marginTop: 16, textAlign: "right" }}
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} items`
            }
          />
        </>
      ) : (
        <Table
          rowClassName={(record, index) =>
            index % 2 === 0 ? "table-row-light" : "table-row-dark"
          }
          className="gx-table-responsive rfq-table myexim-table"
          columns={columns}
          dataSource={data}
          pagination={{
            position: ["bottomRight"],
            showSizeChanger: true,
            pageSize: pageSize,
            total: count,
            onChange: onShowSizeChange,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          onRow={(record) => {
            return {
              onClick: () => {
                handleRowClick(record?.id);
              },
            };
          }}
          loading={loading}
          rowKey="id"
        />
      );
    },
    [
      isMobile,
      columns,
      currentPage,
      pageSize,
      loading,
      onShowSizeChange,
      handleRowClick,
    ]
  );

  return (
    <>
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div className="myexim-table-title">Business Card Requests</div>
            {isArrayNonEmpty(businessCardData) &&
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleSendBusinessRequest}
            >
              {isMobile ? "" : "Send Business Request"}
            </Button> 
            }
          </div>
        }
        style={{ width: "100%" }}
      >

        {isArrayNonEmpty(businessCardData) ? (
        renderContent(businessCardData, totalCount)
      ) : (
        !loading && (
          <ListEmptyComponent
            message="No business card requests have been sent yet."
            buttonText="Send Business Request"
            onButtonClick={handleSendBusinessRequest}
          />
        )
      )}
      
      </Card>
    </>
  );
};

export default BusinessCardRequestsFreightForwarder;
